import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingBag, ChevronDown } from 'lucide-react';
import Marquee from "react-fast-marquee";
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { categoriesStore, Category, settingsStore } from '@/stores/localStorageStore';

interface HeaderProps {
  onCartClick?: () => void;
}

interface NavItem {
  key: string;
  label: string;
  href: string;
  isAnchor?: boolean;
  children?: { key: string; label: string; href: string }[];
}

const Header = ({ onCartClick }: HeaderProps = {}) => {
  const { language, setLanguage, t } = useLanguage();
  const { totalItems, setIsCartOpen } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState<Category[]>(categoriesStore.getAll());
  const [settings, setSettings] = useState(settingsStore.get());

  useEffect(() => {
    setCategories(categoriesStore.getAll());
    const unsubscribeCats = categoriesStore.subscribe((cats) => {
      setCategories([...cats]);
    });

    setSettings(settingsStore.get());
    const unsubscribeSettings = settingsStore.subscribe((newSettings) => {
      setSettings(newSettings);
    });

    return () => {
      unsubscribeCats();
      unsubscribeSettings();
    };
  }, []);

  const announcementsList = useMemo(() => {
    if (!settings.announcements) return [];
    try {
      const parsed = typeof settings.announcements === 'string' ? JSON.parse(settings.announcements) : settings.announcements;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [settings.announcements]);

  // Build navItems dynamically from categories
  const navItems: NavItem[] = [
    { key: 'aboutUs', label: t('aboutUs'), href: '#about', isAnchor: true },
    { key: 'blog', label: 'Blog', href: '/blog', isAnchor: false },
    { 
      key: 'history', 
      label: t('history'), 
      href: '/dostawcy', 
      isAnchor: false,
      children: [
        { key: 'vigilione', label: 'Tenuta Vigilione', href: '/dostawcy/tenuta-vigilione' },
        { key: 'fiore', label: 'Fiore di Puglia', href: '/dostawcy/fiore-di-puglia' },
        { key: 'galantino', label: 'Galantino', href: '/dostawcy/galantino' }
      ]
    },
  ];

  const getCategoryUrl = (category: string) => {
    const seoMap: Record<string, string> = {
        'wines': 'wina',
        'tradycyjne-przekaski': 'tradycyjne-przekaski',
        'italianProducts': 'oliwa',
        'coffee': 'kawa',
        'gifts': 'zestawy',
        'accessories': 'pozostale-produkty'
    };
    const mapped = seoMap[category];
    return mapped ? `/produkty/${mapped}` : `/produkty/${category}`;
  };

  const navCategories = categories.filter(cat => cat.showInNav !== false);
  
  // Słownik skróconych nazw, by oszczędzić miejsce w nawigacji (bez zmieniania w bazie)
  const categoryNameOverrides: Record<string, string> = {
    'tradycyjne-przekaski': 'Przekąski',
    'pozostale-produkty': 'Pozostałe',
    'gifts': 'Prezenty'
  };

  navCategories.forEach(cat => {
    const defaultLabel = categoryNameOverrides[cat.name] || cat.displayName;
    const item: NavItem = {
      key: cat.name,
      label: language === 'EN' && cat.displayNameEn ? cat.displayNameEn : defaultLabel,
      href: getCategoryUrl(cat.name),
    };

    if (cat.subcategories && cat.subcategories.length > 0) {
      item.children = cat.subcategories.map(sub => ({
        key: `${cat.name}-${sub}`,
        label: sub,
        href: `${getCategoryUrl(cat.name)}?subcategory=${sub}`,
      }));
    }

    navItems.push(item);
  });

  navItems.push({ key: 'contact', label: t('contact'), href: '#contact', isAnchor: true });

  const handleNavigation = (href: string, isAnchor?: boolean) => {
    if (isAnchor) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(href);
    }
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  return (
    <header className="fixed top-0 z-50 w-full flex flex-col shadow-sm">
      <AnimatePresence>
        {settings.announcements_enabled !== false && announcementsList.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="w-full bg-primary py-2 text-xs font-semibold tracking-wide text-primary-foreground sm:text-sm overflow-hidden flex"
          >
            {settings.announcements_marquee !== false ? (
              <Marquee gradient={false} speed={40} className="overflow-hidden">
                <div className="flex items-center gap-12 px-6">
                  {announcementsList.map((ann: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-4 whitespace-nowrap">
                      <span>{ann.text}</span>
                      {ann.link && ann.linkText && (
                        <a
                          href={ann.link}
                          className="inline-flex items-center justify-center rounded-full bg-white/20 px-3 py-0.5 text-xs font-bold transition-colors hover:bg-white/30 backdrop-blur-sm"
                        >
                          {ann.linkText}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </Marquee>
            ) : (
              <div className="flex items-center justify-center gap-12 px-4 w-full">
                <div className="flex flex-wrap justify-center gap-6 md:gap-12 w-full">
                  {announcementsList.map((ann: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-4 whitespace-nowrap">
                      <span>{ann.text}</span>
                      {ann.link && ann.linkText && (
                        <a
                          href={ann.link}
                          className="inline-flex items-center justify-center rounded-full bg-white/20 px-3 py-0.5 text-xs font-bold transition-colors hover:bg-white/30 backdrop-blur-sm"
                        >
                          {ann.linkText}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <div className="glass-effect border-b border-border/50">
        <div className="container mx-auto px-4">
          <div className="flex h-[54px] sm:h-[60px] md:h-[80px] items-center justify-between gap-2 min-w-0">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0">
              <span className="font-display text-[18px] sm:text-[22px] md:text-[21px] font-bold tracking-wide sm:tracking-widest text-primary">
                Włoska Piwnica
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden items-center gap-0.5 xl:gap-1 2xl:gap-2 xl:flex">
              {navItems.map((item) => (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => item.children && setActiveDropdown(item.key)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.isAnchor && !item.href.startsWith('/') ? `/${item.href}` : item.href}
                    onClick={(e) => { e.preventDefault(); item.href && handleNavigation(item.href, item.isAnchor); }}
                    className="flex items-center gap-1 px-1.5 py-2 text-[11px] font-medium text-foreground/80 whitespace-nowrap transition-colors hover:text-primary xl:px-2 xl:text-[13px] 2xl:px-3 2xl:text-sm"
                  >
                    {item.label}
                    {item.children && <ChevronDown className="h-3 w-3 shrink-0" />}
                  </Link>

                  {/* Dropdown */}
                  <AnimatePresence>
                    {item.children && activeDropdown === item.key && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 top-full z-50 mt-1.5 min-w-[200px] rounded-lg border border-border bg-card p-2.5 shadow-lg"
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.key}
                            to={child.href}
                            onClick={(e) => { e.preventDefault(); handleNavigation(child.href); }}
                            className="block w-full rounded-md px-4 py-2 text-left text-sm text-foreground/80 transition-colors hover:bg-muted hover:text-primary whitespace-normal"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-5">
              {/* Language Switch */}
              <div className="hidden min-[360px]:flex items-center gap-1.5 rounded-full border border-border bg-card/80 p-1.5">
                <button
                  onClick={() => setLanguage('PL')}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-all ${language === 'PL'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                  PL
                </button>
                <button
                  onClick={() => setLanguage('EN')}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-all ${language === 'EN'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                  EN
                </button>
              </div>

              {/* Cart Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                animate={totalItems > 0 ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.3 }}
                onClick={() => onCartClick ? onCartClick() : setIsCartOpen(true)}
                aria-label={t('cart') || 'Koszyk'}
                className="relative overflow-visible rounded-full bg-primary p-3 text-primary-foreground shadow-md transition-shadow hover:shadow-lg"
              >
                <ShoppingBag className="h-6 w-6" />
                <AnimatePresence>
                  {totalItems > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground"
                    >
                      {totalItems}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}
                className="rounded-lg p-2.5 xl:hidden"
              >
                {isMobileMenuOpen ? (
                  <X className="h-7 w-7" />
                ) : (
                  <Menu className="h-7 w-7" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-b border-border bg-card xl:hidden overflow-hidden"
          >
            <nav className="container mx-auto px-4 py-4 max-h-[85vh] overflow-y-auto">
              {navItems.map((item) => (
                <div key={item.key}>
                  <Link
                    to={item.isAnchor && !item.href.startsWith('/') ? `/${item.href}` : item.href}
                    onClick={(e) => { e.preventDefault(); item.href && handleNavigation(item.href, item.isAnchor); }}
                    className="block w-full py-3 text-left text-foreground/80 transition-colors hover:text-primary"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="ml-4 border-l border-border pl-4">
                      {item.children.map((child) => (
                        <Link
                          key={child.key}
                          to={child.href}
                          onClick={(e) => { e.preventDefault(); handleNavigation(child.href); }}
                          className="block w-full py-2 text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Mobile Language Switcher (visible only on very small screens) */}
              <div className="mt-4 border-t border-border pt-4 flex min-[360px]:hidden items-center gap-2">
                <span className="text-sm text-muted-foreground mr-2">Język:</span>
                <button
                  onClick={() => { setLanguage('PL'); setIsMobileMenuOpen(false); }}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${language === 'PL'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                >
                  PL
                </button>
                <button
                  onClick={() => { setLanguage('EN'); setIsMobileMenuOpen(false); }}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${language === 'EN'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                >
                  EN
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
